import { useState, useMemo, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell
} from 'recharts';

const COLORS = [
  '#2563eb', '#16a34a', '#dc2626', '#7c3aed', '#ea580c',
  '#0891b2', '#65a30d', '#db2777', '#d97706', '#4f46e5',
  '#0d9488', '#9333ea', '#b45309', '#0284c7', '#15803d'
];

function ExpenseBreakdown({ transactions }) {
  const [groupBy, setGroupBy] = useState('category');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [minDate, setMinDate] = useState('');
  const [maxDate, setMaxDate] = useState('');

  const toInputDate = (d) => d.toISOString().split('T')[0];
  const fmt = (v) => `$${Math.abs(v).toLocaleString('en-AU', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  useEffect(() => {
    if (transactions.length === 0) return;
    const allDates = transactions.map(t => new Date(t.date));
    const min = toInputDate(new Date(Math.min(...allDates)));
    const max = toInputDate(new Date(Math.max(...allDates)));
    setMinDate(min);
    setMaxDate(max);
    setDateFrom(min);
    setDateTo(max);
  }, [transactions]);

  const expenses = useMemo(() => {
    return transactions.filter(t => {
      const date = new Date(t.date);
      const from = dateFrom ? new Date(dateFrom) : null;
      const to = dateTo ? new Date(dateTo) : null;
      const matchesFrom = !from || date >= from;
      const matchesTo = !to || date <= to;
      return t.type === 'expense' && matchesFrom && matchesTo;
    });
  }, [transactions, dateFrom, dateTo]);

  const chartData = useMemo(() => {
    if (groupBy === 'category') {
      // Group by category, then break down by subCategory
      const grouped = {};
      expenses.forEach(t => {
        const cat = t.category || 'Uncategorised';
        const sub = t.subCategory || 'Other';
        if (!grouped[cat]) grouped[cat] = { total: 0, subs: {} };
        grouped[cat].total += Math.abs(Number(t.amount));
        if (!grouped[cat].subs[sub]) grouped[cat].subs[sub] = 0;
        grouped[cat].subs[sub] += Math.abs(Number(t.amount));
      });

      return Object.entries(grouped)
        .map(([name, data]) => ({
          name,
          value: parseFloat(data.total.toFixed(2)),
          subs: Object.entries(data.subs)
            .map(([subName, subValue]) => ({ name: subName, value: parseFloat(subValue.toFixed(2)) }))
            .sort((a, b) => b.value - a.value)
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 20);
    }

    // description or month — flat grouping
    const grouped = {};
    expenses.forEach(t => {
      let key;
      if (groupBy === 'description') key = t.description || 'Unknown';
      else if (groupBy === 'month') {
        const d = new Date(t.date);
        key = new Date(d.getFullYear(), d.getMonth(), 1)
          .toLocaleDateString('en-AU', { month: 'short', year: '2-digit' });
      }
      if (!grouped[key]) grouped[key] = 0;
      grouped[key] += Math.abs(Number(t.amount));
    });

    return Object.entries(grouped)
      .map(([name, value]) => ({ name, value: parseFloat(value.toFixed(2)), subs: [] }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 20);
  }, [expenses, groupBy]);

  const total = chartData.reduce((sum, d) => sum + d.value, 0);

  const inputStyle = {
    padding: '6px 10px',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    background: 'var(--bg)',
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '13px',
    color: 'var(--text)',
    outline: 'none'
  };

  const labelStyle = {
    fontFamily: 'DM Mono, monospace',
    fontSize: '10px',
    textTransform: 'uppercase',
    color: 'var(--muted)',
    letterSpacing: '0.5px',
    marginBottom: '4px'
  };

  if (transactions.length === 0) return null;

  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius)',
      padding: '24px',
      marginBottom: '28px'
    }}>
      <div style={{ fontFamily: 'DM Mono, monospace', fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: '20px' }}>
        Expense Breakdown
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={labelStyle}>Group By</div>
          <select
            value={groupBy}
            onChange={e => setGroupBy(e.target.value)}
            style={inputStyle}
          >
            <option value="category">Category</option>
            <option value="description">Description</option>
            <option value="month">Month</option>
          </select>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={labelStyle}>From</div>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={labelStyle}>To</div>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputStyle} />
        </div>

        <button
          onClick={() => { setDateFrom(minDate); setDateTo(maxDate); }}
          style={{ padding: '6px 12px', border: '1px solid var(--border)', borderRadius: '4px', background: 'transparent', fontFamily: 'DM Mono, monospace', fontSize: '11px', color: 'var(--muted)', cursor: 'pointer' }}
        >
          reset
        </button>
      </div>

      <div style={{ fontFamily: 'DM Mono, monospace', fontSize: '11px', color: 'var(--muted)', marginBottom: '16px' }}>
        {chartData.length} groups · total {fmt(total)}
      </div>

      {chartData.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', fontFamily: 'DM Mono, monospace', fontSize: '12px', color: 'var(--muted)' }}>
          no expenses for selected range
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e8e6e1" vertical={false} />
              <XAxis
                dataKey="name"
                tick={{ fontFamily: 'DM Mono, monospace', fontSize: 10, fill: '#9b9690' }}
                axisLine={false}
                tickLine={false}
                angle={-45}
                textAnchor="end"
                interval={0}
              />
              <YAxis
                tick={{ fontFamily: 'DM Mono, monospace', fontSize: 11, fill: '#9b9690' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={fmt}
              />
              <Tooltip
                formatter={(value) => [fmt(value), 'Amount']}
                contentStyle={{ fontFamily: 'DM Mono, monospace', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '4px', background: 'white' }}
              />
              <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          {/* Legend with subcategory breakdown */}
          <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {chartData.map((entry, index) => (
              <div key={index}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: COLORS[index % COLORS.length], flexShrink: 0 }} />
                  <div style={{ flex: 1, fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: 'var(--text)', fontWeight: '500' }}>{entry.name}</div>
                  <div style={{ fontFamily: 'DM Mono, monospace', fontSize: '11px', color: 'var(--muted)' }}>{((entry.value / total) * 100).toFixed(1)}%</div>
                  <div style={{ fontFamily: 'DM Mono, monospace', fontSize: '11px', color: 'var(--text)', minWidth: '80px', textAlign: 'right' }}>{fmt(entry.value)}</div>
                </div>
                {/* Subcategory breakdown */}
                {entry.subs && entry.subs.length > 1 && entry.subs.map((sub, si) => (
                  <div key={si} style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingLeft: '20px', marginTop: '3px' }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: COLORS[index % COLORS.length], opacity: 0.4, flexShrink: 0 }} />
                    <div style={{ flex: 1, fontFamily: 'DM Sans, sans-serif', fontSize: '11px', color: 'var(--muted)' }}>{sub.name}</div>
                    <div style={{ fontFamily: 'DM Mono, monospace', fontSize: '11px', color: 'var(--muted)' }}>{((sub.value / entry.value) * 100).toFixed(1)}%</div>
                    <div style={{ fontFamily: 'DM Mono, monospace', fontSize: '11px', color: 'var(--muted)', minWidth: '80px', textAlign: 'right' }}>{fmt(sub.value)}</div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default ExpenseBreakdown;